// src/AdminPage.js
import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Edit2, X, Check } from 'lucide-react';
import { api } from './api';
import { useAuth } from './AuthContext';

const ROLES = ['admin', 'editor', 'readonly'];

export default function AdminPage() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [newUser, setNewUser] = useState({ username: '', password: '', role: 'editor' });
  const [editingId, setEditingId] = useState(null);
  const [editRole, setEditRole] = useState('');

  useEffect(() => {
    loadUsers();
  }, []);

  async function loadUsers() {
    try {
      setUsers(await api.getUsers());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleCreate(e) {
    e.preventDefault();
    setError('');
    try {
      await api.createUser(newUser);
      setNewUser({ username: '', password: '', role: 'editor' });
      setShowAddForm(false);
      await loadUsers();
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleRoleUpdate(id) {
    setError('');
    try {
      await api.updateUser(id, { role: editRole });
      setEditingId(null);
      await loadUsers();
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleDelete(id) {
    if (!window.confirm('Delete this user?')) return;
    setError('');
    try {
      await api.deleteUser(id);
      await loadUsers();
    } catch (err) {
      setError(err.message);
    }
  }

  if (loading) return <div className="p-8 text-slate-400">Loading...</div>;

  return (
    <div className="max-w-2xl mx-auto p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-slate-800">User Management</h2>
        <button
          onClick={() => setShowAddForm(v => !v)}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium"
        >
          <Plus size={16} />
          Add User
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 mb-4 text-sm">
          {error}
        </div>
      )}

      {showAddForm && (
        <form onSubmit={handleCreate} className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-4">
          <h3 className="font-medium text-slate-700 mb-3">New User</h3>
          <div className="flex gap-3 flex-wrap">
            <input
              placeholder="Username"
              value={newUser.username}
              onChange={e => setNewUser(u => ({ ...u, username: e.target.value }))}
              required
              className="flex-1 min-w-32 border border-slate-300 rounded px-3 py-1.5 text-sm"
            />
            <input
              placeholder="Password (min 8 chars)"
              type="password"
              value={newUser.password}
              onChange={e => setNewUser(u => ({ ...u, password: e.target.value }))}
              required
              className="flex-1 min-w-40 border border-slate-300 rounded px-3 py-1.5 text-sm"
            />
            <select
              value={newUser.role}
              onChange={e => setNewUser(u => ({ ...u, role: e.target.value }))}
              className="border border-slate-300 rounded px-3 py-1.5 text-sm"
            >
              {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
            <button type="submit" className="bg-green-600 hover:bg-green-700 text-white px-4 py-1.5 rounded text-sm font-medium">
              Create
            </button>
            <button type="button" onClick={() => setShowAddForm(false)} className="text-slate-500 hover:text-slate-700 px-2">
              Cancel
            </button>
          </div>
        </form>
      )}

      <div className="bg-white border border-slate-200 rounded-lg divide-y divide-slate-100">
        {users.map(u => (
          <div key={u.id} className="flex items-center justify-between px-4 py-3">
            <div className="flex items-center gap-3">
              <span className="font-medium text-slate-800">{u.username}</span>
              {u.id === currentUser?.id && (
                <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">you</span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {editingId === u.id ? (
                <>
                  <select
                    value={editRole}
                    onChange={e => setEditRole(e.target.value)}
                    className="border border-slate-300 rounded px-2 py-1 text-sm"
                  >
                    {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                  <button onClick={() => handleRoleUpdate(u.id)} className="text-green-600 hover:text-green-800 p-1">
                    <Check size={16} />
                  </button>
                  <button onClick={() => setEditingId(null)} className="text-slate-400 hover:text-slate-600 p-1">
                    <X size={16} />
                  </button>
                </>
              ) : (
                <>
                  <span className={`text-xs px-2 py-0.5 rounded font-medium ${
                    u.role === 'admin' ? 'bg-purple-100 text-purple-700' :
                    u.role === 'editor' ? 'bg-blue-100 text-blue-700' :
                    'bg-slate-100 text-slate-600'
                  }`}>{u.role.charAt(0).toUpperCase() + u.role.slice(1)}</span>
                  <button
                    onClick={() => { setEditingId(u.id); setEditRole(u.role); }}
                    className="text-slate-400 hover:text-slate-600 p-1"
                    title="Edit role"
                    aria-label="Edit role"
                  >
                    <Edit2 size={15} />
                  </button>
                  <button
                    onClick={() => handleDelete(u.id)}
                    className="text-slate-400 hover:text-red-600 p-1"
                    title="Delete user"
                    aria-label="Delete user"
                    disabled={u.id === currentUser?.id}
                  >
                    <Trash2 size={15} />
                  </button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
